import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { getRasterMapProvider } from '../config/mapProviders';

// Fix default marker icon path issues in Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Leaflet only speaks raster XYZ, so a mapbox/google selection degrades to OSM here.
const PROVIDER = getRasterMapProvider();
// Scene creation just needs a basemap — the provider's first variant.
const TILES = PROVIDER.variants[0];

interface Props {
  value: { latitude: number; longitude: number; name?: string } | null;
  onChange: (value: { latitude: number; longitude: number; name?: string }) => void;
}

// Helper to map full state names to US postal abbreviations (partial list, extend as needed)
const US_STATE_MAP: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO',
  Connecticut: 'CT', Delaware: 'DE', Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID',
  Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR',
  Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD',
  Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT', Virginia: 'VA', Washington: 'WA',
  'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};

// Helper to abbreviate common words in street addresses
function abbreviateStreet(street: string): string {
  if (!street) return street;
  const replacements: Record<string, string> = {
    Avenue: 'Ave', Street: 'St', Road: 'Rd', Lane: 'Ln', Drive: 'Dr', Boulevard: 'Blvd',
    Parkway: 'Pkwy', Place: 'Pl', Northeast: 'NE', Northwest: 'NW', Southeast: 'SE',
    Southwest: 'SW', North: 'N', South: 'S', East: 'E', West: 'W',
  };
  let result = street;
  for (const [word, abbr] of Object.entries(replacements)) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    result = result.replace(regex, abbr);
  }
  return result;
}

// Build a concise address string from Nominatim's address object
function formatAddress(address: any): string {
  if (!address) return '';
  const street = abbreviateStreet(
    [address.house_number, address.road].filter(Boolean).join(' ').trim()
  );

  const city = address.city || address.town || address.village || address.hamlet || '';
  let state = address.state_code || '';
  if (!state && address.state && US_STATE_MAP[address.state]) {
    state = US_STATE_MAP[address.state];
  }
  const postcode = address.postcode || '';
  const country = (address.country_code ? address.country_code.toUpperCase() : address.country) || '';

  // Assemble parts, removing empties
  return [street, city, state, postcode, country].filter(Boolean).join(', ');
}

// Update the result type to include address details
interface PlaceResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: string;
  address?: any;
}

// Helper for Nominatim search
async function searchPlaces(query: string): Promise<PlaceResult[]> {
  if (!query) return [];
  const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(query)}`;
  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
  if (!res.ok) return [];
  return (await res.json()) as any;
}

const LocationMarker: React.FC<{ position: [number, number] | null; onChange: (pos: [number, number]) => void }> = ({ position, onChange }) => {
  useMapEvents({
    click(e: any) {
      onChange([e.latlng.lat, e.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
};

const MapUpdater: React.FC<{ position: [number, number] | null }> = ({ position }) => {
  const map = useMap();
  React.useEffect(() => {
    if (position) {
      map.setView(position, 16); // Zoom in a bit closer
    }
  }, [position, map]);
  return null;
};

const MapPicker: React.FC<Props> = ({ value, onChange }) => {
  const [position, setPosition] = useState<[number, number] | null>(value ? [value.latitude, value.longitude] : null);
  const [query, setQuery] = useState(value?.name || '');
  const [results, setResults] = useState<PlaceResult[]>([]);

  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      const res = await searchPlaces(q);
      setResults(res);
    }, 400),
    []
  );

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const reverseGeocode = async (pos: [number, number]) => {
    try {
      const revUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos[0]}&lon=${pos[1]}&zoom=18&addressdetails=1`;
      const r = await fetch(revUrl, { headers: { 'Accept-Language': 'en' } });
      if (r.ok) {
        const data = await r.json();
        return formatAddress(data?.address) || data?.display_name || '';
      }
    } catch {}
    return '';
  };

  const handlePosChange = async (pos: [number, number], address?: string) => {
    setPosition(pos);
    const locationName = address || await reverseGeocode(pos) || `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}`;
    onChange({ latitude: pos[0], longitude: pos[1], name: locationName });
    setQuery(locationName);
    setResults([]);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((geo) => {
      const pos: [number, number] = [geo.coords.latitude, geo.coords.longitude];
      handlePosChange(pos);

      // Reverse geocode to get address and fill search box
      (async () => {
        try {
          const revUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos[0]}&lon=${pos[1]}&zoom=18&addressdetails=1`;
          const r = await fetch(revUrl, { headers: { 'Accept-Language': 'en' } });
          if (r.ok) {
            const data = await r.json();
            if (data && data.display_name) {
              setQuery(data.display_name);
            } else {
              setQuery('Current location');
            }
          } else {
            setQuery('Current location');
          }
        } catch {
          setQuery('Current location');
        }
        setResults([]);
      })();
    });
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    const typedQuery = query.trim();
    if (!typedQuery) return;

    let list = results;
    if (!list || list.length === 0) {
      try {
        list = await searchPlaces(typedQuery);
      } catch {
        list = [];
      }
    }

    if (list && list.length > 0) {
      const r = list[0];
      const lat = parseFloat(r.lat);
      const lon = parseFloat(r.lon);
      const formatted = formatAddress(r.address) || r.display_name;
      handlePosChange([lat, lon], formatted);
    }
  };

  return (
    <div className="space-y-3 bg-[#161513]/50 p-4 rounded-xl border border-[#0CB4E4]/20">
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Search for a location or address..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 w-full px-4 py-2.5 bg-[#161513] border border-[#0CB4E4]/30 text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0CB4E4] focus:border-[#0CB4E4]"
        />
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="px-4 py-2.5 bg-[#0CB4E4]/80 text-white text-sm font-medium rounded-lg hover:bg-[#0C5484] transition-colors shadow-md"
        >
          Current location
        </button>
      </div>

      {results.length > 0 && (
        <div className="bg-[#161513] border border-[#0CB4E4]/30 rounded-lg max-h-52 overflow-y-auto text-sm">
          {results.map((r) => {
            const formatted = formatAddress(r.address) || r.display_name;
            return (
              <button
                key={r.place_id}
                type="button"
                className="block w-full text-left px-4 py-3 text-gray-200 hover:bg-[#0CB4E4]/20 cursor-pointer border-b border-[#0CB4E4]/20 last:border-b-0 transition-colors"
                onClick={() => {
                  const lat = parseFloat(r.lat);
                  const lon = parseFloat(r.lon);
                  handlePosChange([lat, lon], formatted);
                }}
              >
                {formatted}
              </button>
            );
          })}
        </div>
      )}

      <div className="h-72 w-full rounded-lg overflow-hidden border border-[#0CB4E4]/30">
        <MapContainer
          center={position || [20, 0]}
          zoom={position ? 13 : 2}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url={TILES.url}
            attribution={PROVIDER.attribution}
            subdomains={PROVIDER.subdomains ?? 'abc'}
            maxZoom={PROVIDER.maxZoom ?? 19}
          />
          <LocationMarker position={position} onChange={handlePosChange} />
          <MapUpdater position={position} />
        </MapContainer>
      </div>
    </div>
  );
};

export default MapPicker;
